import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCcwIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';

export interface PastDueBannerProps {
  onPayNow?: () => void;
}

/**
 * Amber banner shown above BillingOverviewPage when
 * `subscription.status === 'past_due'` AND the grace window is still
 * open. Pairs visually with `SubscriptionRenewalBadge` (header, soft
 * severity) — both use the same amber palette and refresh icon so the
 * user reads them as one continuous nudge.
 *
 * No countdown / deadline is displayed — by design. Countdowns read
 * as "you have time to pay later" and users defer. We render a flat
 * "subscription expired, pay now" framing instead. See the matching
 * note in `useSubscriptionRenewalGuard`.
 *
 * Note: past-grace ("blocked") is NOT this banner — the overview
 * switches to `viewMode === 'paywall'` and renders `BillingPaywall`
 * instead. So this banner always represents the "renewal needed but
 * still recoverable" state and should never use red-alert styling.
 *
 * Single CTA wires to the manager hook which opens the latest unpaid
 * Billing's hosted-checkout URL.
 */
export function PastDueBanner({ onPayNow }: PastDueBannerProps) {
  const { t } = useTranslation('m-billing');
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <RefreshCcwIcon className="!h-4 !w-4" />
        </span>
        <div className="flex flex-col gap-0.5">
          <h4 className="text-sm font-semibold text-amber-900">
            {t('subscription.past_due_banner.title')}
          </h4>
          <p className="text-sm text-amber-800">
            {t('subscription.past_due_banner.description')}
          </p>
        </div>
      </div>
      {onPayNow && (
        <Button onClick={onPayNow} className="self-start sm:self-auto">
          {t('subscription.past_due_banner.cta')}
        </Button>
      )}
    </div>
  );
}
